import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { PageWrapper } from '../../components';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import {
  accountsSlice,
  fetchAccounts,
  sumUpAccountMembers,
  accountsSelectors,
  GetAccountsData,
  selectAccountsPageFilters,
  AccountsPageFiltersInitialState,
  getOperationalCostList,
} from '../../redux/accounts';
import { AccountsTable } from './AccountsTable/AccountsTable';
import { userDataSelectors } from '../../redux/userData';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { PageTitle } from '../../utils/pageTitles';
import { useTitle } from '../../hooks/useTitle';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import { AccountReportsPopper } from './AccountReportsPopper/AccountReportsPopper';
import { Info } from './BudgetsHeader/Info/Info';
import { CustomNumber } from './BudgetsHeader/Number/Number';
import { AccountOverview, AccountStatus } from '../../types';
import { MarginSummary } from './BudgetsHeader/MarginSummary/MarginSummary';
import { PortfolioMoneyStat } from './BudgetsHeader/PortfolioMoneyStat/PortfolioMoneyStat';
import {
  HEADER_VALUE_GAP,
  HEADER_VALUE_LINE_HEIGHT,
  headerCountStyle,
  headerValueStyle,
} from './BudgetsHeader/headerStyles';
import WorkOffOutlinedIcon from '@mui/icons-material/WorkOffOutlined';
import { FilterDrawer } from './AccountsFilters/AccountsFilters';
import { isEqual } from 'lodash';
import { priceFormat } from '../../components/tableCellRenders';
import { getCurrentYear } from '../../utils';
import { currentYearRevenuePrompt } from '../../redux/account';
import CustomDatePicker from '../../components/datePicker/CustomDatePicker';
import { DateTime } from 'luxon';
import { CustomIcon, IconName } from '../../components/customIcon/CustomIcon';
import EditOperationalCostModal from '../editOperationalCost/EditOperationalCost';

export const AccountsList = () => {
  const setPageTitle = useTitle();

  useEffect(() => {
    setPageTitle(PageTitle.portfolio);
  }, [setPageTitle]);

  const dispatch = useAppDispatch();
  const loading = useAppSelector(accountsSelectors.getLoading);
  const accounts = useAppSelector(accountsSelectors.getProcessedAccounts);
  const accountsPageFilters = useAppSelector(selectAccountsPageFilters);
  const accountsPageFiltersWithoutFilterBy = accountsPageFilters;
  const accountsPageFiltersInitialStateWithoutFilterBy =
    AccountsPageFiltersInitialState;
  const pdmsToFilter = accountsPageFilters.PDMsToFilter;
  const dmsToFilter = accountsPageFilters.DMsToFilter;

  const user = useAppSelector(userDataSelectors.getUserData);
  const dateFilterValue = useAppSelector(accountsSelectors.getDateFilterValue);

  const errMessage = useAppSelector(
    accountsSelectors.getAccountsListFetchErrorMessage,
  );

  const isCLevelUser = user?.permissions.seesAllAccounts;
  const [isDrawerOpen, setDrawerOpen] = useState<boolean>(false);
  const canGenerateMarginReports = useMemo(
    () =>
      user?.permissions.isFinance ||
      (user?.permissions.hasAccounts &&
        user?.permissions.isBillRateManager &&
        user.permissions.isCompensationManager),
    [user],
  );

  useEffect(() => {
    dispatch(
      fetchAccounts(
        getFetchAccountsParams({
          pdmsToFilter,
          dmsToFilter,
          accountStatus: accountsPageFilters.accountStatus,
          dateFilterValue,
        }),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    pdmsToFilter,
    dmsToFilter,
    accountsPageFilters.accountStatus,
    dateFilterValue,
  ]);

  const handleFiltersOnClick = useCallback(() => {
    setDrawerOpen(true);
  }, [setDrawerOpen]);

  const handleFiltersOnClose = useCallback(() => {
    setDrawerOpen(false);
  }, [setDrawerOpen]);

  const resetFilters = useCallback(() => {
    dispatch(accountsSlice.actions.cleanDateFilterValues());

    dispatch(
      fetchAccounts(
        getFetchAccountsParams({
          accountStatus: AccountsPageFiltersInitialState.accountStatus,
          dateFilterValue: null,
          pdmsToFilter,
          dmsToFilter,
        }),
      ),
    );
    dispatch(accountsSlice.actions.resetAccountsPageFilters());
  }, [dispatch, dmsToFilter, pdmsToFilter]);

  const isFiltersActive = !isEqual(
    accountsPageFiltersInitialStateWithoutFilterBy,
    accountsPageFiltersWithoutFilterBy,
  );

  return (
    <PageWrapper
      isLoading={loading}
      sx={{
        pt: 0,
        pb: 0,
        px: 2,
        width: 'fit-content',
        minWidth: '100%',
      }}
    >
      <FilterDrawer
        isFiltersActive={isFiltersActive}
        isOpen={isDrawerOpen}
        onClose={handleFiltersOnClose}
        resetFilters={resetFilters}
      />

      {!accounts.length && !errMessage && (
        <Box
          display="flex"
          justifyContent="center"
          alignContent="center"
          sx={{
            height: '90%',
            backgroundColor: 'white',
            display: 'grid',
            verticalAlign: 'middle',
          }}
        >
          <Typography variant="h1" mx="2" color="darkslategray">
            {"You don't have any accounts assigned to you"}
          </Typography>
        </Box>
      )}

      <BudgetsHeaderForList
        accounts={accounts}
        handleFiltersOnClick={handleFiltersOnClick}
        errMessage={errMessage}
        isFiltersActive={isFiltersActive}
        dateFilterValue={dateFilterValue}
        resetFilters={resetFilters}
        canGenerateMarginReports={canGenerateMarginReports}
      />
      {!(!accounts.length && !errMessage) && (
        <>
          <AccountsTable
            accounts={accounts}
            isCLevelUser={isCLevelUser ?? false}
          />
          {accountsPageFilters.accountStatus === AccountStatus.Active && (
            <Stack
              sx={{
                mb: '40px',
                bottom: 0,
                padding: '16px 0px 16px 16px',
                bgcolor: 'background.default',
                width: '100%',
              }}
            >
              <Stack flexDirection="row" gap="8px">
                <WorkOffOutlinedIcon color="action" />
                <Button
                  variant="text"
                  sx={{
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    color: 'black',
                    fontSize: '14px',
                    padding: '0',
                    background: 'none !important',
                  }}
                  onClick={() => {
                    dispatch(
                      accountsSlice.actions.setAccountsPageFilters({
                        accountStatus: null,
                      }),
                    );
                  }}
                >
                  Show closed accounts
                </Button>
              </Stack>
            </Stack>
          )}
        </>
      )}
    </PageWrapper>
  );
};

interface BudgetFilterHeaderProps {
  accounts: AccountOverview[];
  handleFiltersOnClick: () => void;
  canGenerateMarginReports?: boolean;
  errMessage?: string | null;
  dateFilterValue: string | null;
  isFiltersActive: boolean;
  resetFilters: () => void;
}

const BudgetsHeaderForList = ({
  accounts,
  handleFiltersOnClick,
  canGenerateMarginReports,
  errMessage,
  dateFilterValue,
  isFiltersActive,
  resetFilters,
}: BudgetFilterHeaderProps) => {
  const reportsAnchor = React.useRef<HTMLButtonElement | null>(null);
  const [isReportsPopperOpen, setReportsPopperOpen] = useState<boolean>(false);
  const [isOperationalCostModalOpen, setOperationalCostModalOpen] =
    useState(false);

  const dateFilterValueLocal = useAppSelector(
    accountsSelectors.getDateFilterValueLocal,
  );

  const operationalCostList = useAppSelector(getOperationalCostList);

  const user = useAppSelector(userDataSelectors.getUserData);

  const toggleReportsPopper = useCallback(() => {
    setReportsPopperOpen(!isReportsPopperOpen);
  }, [setReportsPopperOpen, isReportsPopperOpen]);

  const handlePopperClose = useCallback(() => {
    setReportsPopperOpen(false);
  }, []);

  const handleToggleOperationalCostModal = useCallback(() => {
    setOperationalCostModalOpen(!isOperationalCostModalOpen);
  }, [isOperationalCostModalOpen, setOperationalCostModalOpen]);

  const flagsCount = useMemo(() => {
    return accounts?.reduce(
      (sum: number, obj: AccountOverview) =>
        sum + (obj?.flagsCount?.red ?? 0) + (obj?.flagsCount?.yellow ?? 0),
      0,
    );
  }, [accounts]);

  const positionsCount = useMemo(() => {
    return accounts?.reduce(
      (sum: number, obj: AccountOverview) => sum + (obj?.openPositions ?? 0),
      0,
    );
  }, [accounts]);

  const projectCount = useMemo(() => {
    return accounts?.reduce(
      (sum: number, obj: any) => sum + obj.projects?.length,
      0,
    );
  }, [accounts]);

  const peopleCount = useMemo(() => {
    return accounts?.reduce(
      (sum: number, obj: any) => sum + sumUpAccountMembers(obj),
      0,
    );
  }, [accounts]);

  const operationalCostForSelectedPeriod = useMemo(() => {
    const currentPeriod = dateFilterValueLocal
      ? Number(DateTime.fromISO(dateFilterValueLocal).toFormat('yyyyMM'))
      : Number(DateTime.now().toFormat('yyyyMM'));

    const operationalCost = operationalCostList
      ? [...operationalCostList]
          .sort((a, b) => b.period - a.period)
          ?.find((data) => data.period <= currentPeriod)
      : {
          period: currentPeriod,
          operationalCost: 11,
        };
    return (
      operationalCost ?? {
        operationalCost: 11,
        period: currentPeriod,
      }
    );
  }, [operationalCostList, dateFilterValueLocal]);

  return (
    <Stack sx={{ width: '100%' }}>
      <Box
        sx={(theme) => ({
          display: 'flex',
          height: theme.spacing(8),
          padding: theme.spacing(1, 1),
          borderRadius: theme.spacing(1, 1, 0, 0),
          alignItems: 'center',
        })}
      >
        <Box>
          <Typography variant="h2">Portfolio overview</Typography>
        </Box>
        <Box display="flex" alignItems="center" ml="auto">
          {/* Account reports button */}
          {!!canGenerateMarginReports && !errMessage && accounts.length && (
            <>
              <Button
                variant="text"
                sx={{
                  color: 'black',
                  mr: '8px',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleReportsPopper();
                }}
                ref={reportsAnchor}
              >
                <CalculateOutlinedIcon />
                &nbsp; Reports
              </Button>
              <AccountReportsPopper
                open={isReportsPopperOpen}
                handlePopperClose={handlePopperClose}
                anchorEl={reportsAnchor}
              />
            </>
          )}
          {/* Date filter */}
          <CustomDatePicker
            setDateFilter={{
              type: 'dispatch',
              func: accountsSlice.actions.setDateFilterValueLocal,
              setGlobalFunc: accountsSlice.actions.setDateFilterValue,
              clean: accountsSlice.actions.cleanDateFilterValues,
            }}
            dateFilterValueLocal={dateFilterValueLocal}
            {...(dateFilterValueLocal && { buttonColor: 'secondary' })}
            buttonSx={{ mr: '8px' }}
          />
          {/* Filter button */}
          <Button
            sx={{
              ...(!isFiltersActive && { color: 'black' }),
              mr: '8px',
            }}
            variant={isFiltersActive ? 'contained' : 'text'}
            onClick={handleFiltersOnClick}
            {...(isFiltersActive && { color: 'secondary' })}
          >
            <FilterListIcon />
            &nbsp; Filters
            <ChevronRightIcon />
          </Button>
          <Button
            variant="text"
            onClick={resetFilters}
            sx={{
              ...(!(isFiltersActive || dateFilterValue !== null) && {
                display: 'none',
                visibility: 'hidden',
              }),
              mr: '8px',
              backgroundColor: '#9c27b020',
            }}
            {...((dateFilterValueLocal || isFiltersActive) && {
              color: 'secondary',
            })}
          >
            <RemoveCircleOutlineIcon fill="#448aff" />
            &nbsp; Reset
          </Button>
        </Box>
      </Box>
      <Box
        sx={(theme) => ({
          display: 'flex',
          backgroundColor: '#fff',
          boxShadow: `0px 3px 5px -1px rgba(102, 95, 95, 0.15)`,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          minHeight: theme.spacing(11),
          padding: theme.spacing(1),
          borderRadius: 2,
          columnGap: '8px',
          rowGap: theme.spacing(1),
          mb: 1,
        })}
      >
        <Info title="Accounts">
          <CustomNumber
            value={accounts.length}
            typographyStyle={headerCountStyle}
          >
            {accounts.length}
          </CustomNumber>
        </Info>
        <Info title="Teams">
          <CustomNumber value={projectCount} typographyStyle={headerCountStyle}>
            {projectCount ? projectCount : 0}
          </CustomNumber>
        </Info>
        <Info title="Headcount">
          <CustomNumber value={peopleCount} typographyStyle={headerCountStyle}>
            {peopleCount ? peopleCount : 0}
          </CustomNumber>
        </Info>

        <MarginSummary
          marginHighlights={accounts[0]?.grossMarginHighlights}
          grossMargin={accounts[0]?.portfolioMargins?.grossMargin}
          netMarginHighlights={accounts[0]?.netMarginHighlights}
          netMargin={accounts[0]?.portfolioMargins?.netMargin}
          valueLineHeight={HEADER_VALUE_LINE_HEIGHT}
          newGrossMargin={accounts[0]?.portfolioMargins?.newGrossMargin}
          newNetMargin={accounts[0]?.portfolioMargins?.newNetMargin}
        />

        <PortfolioMoneyStat
          title={`${getCurrentYear(
            accounts[0]?.portfolioMargins?.currentYear,
          )} revenue`}
          value={accounts[0]?.portfolioMargins?.pastRevenueCurrentYear}
          tooltip={currentYearRevenuePrompt}
        />
        <PortfolioMoneyStat
          title="Lifetime revenue"
          value={accounts[0]?.portfolioMargins?.pastRevenue}
        />
        <PortfolioMoneyStat
          title="Forecasted revenue"
          value={accounts[0]?.portfolioMargins?.projectedRevenue}
        />

        {/* Operational cost */}

        <Info title="Operational cost">
          <Box display="flex" alignItems="center" pt={HEADER_VALUE_GAP}>
            <CustomNumber
              typographyStyle={{ ...headerValueStyle, fontWeight: 600 }}
            >
              {priceFormat({
                value: operationalCostForSelectedPeriod.operationalCost,
                hideCents: true,
                showPeriod: true,
              })}
            </CustomNumber>
            {(user?.permissions.isFinanceAdmin ||
              user?.permissions.isFinanceManager) && (
              <Tooltip title="Edit operational cost" placement="top">
                <IconButton
                  size="small"
                  onClick={handleToggleOperationalCostModal}
                >
                  <CustomIcon
                    icon={IconName.Edit}
                    sx={{ color: '#3F8CFF', fontSize: '20px' }}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Info>

        <Info title="Positions">
          <CustomNumber
            value={positionsCount}
            typographyStyle={headerCountStyle}
          >
            {positionsCount ? positionsCount : 0}
          </CustomNumber>
        </Info>
        <Info title="Flags">
          <CustomNumber value={flagsCount} typographyStyle={headerCountStyle}>
            {flagsCount ? flagsCount : 0}
          </CustomNumber>
        </Info>

        <EditOperationalCostModal
          handleCloseModal={handleToggleOperationalCostModal}
          isModalOpen={isOperationalCostModalOpen}
          operationalCostList={operationalCostList}
        />
      </Box>
    </Stack>
  );
};

const getFetchAccountsParams = ({
  pdmsToFilter,
  dmsToFilter,
  dateFilterValue,
  accountStatus,
}: {
  pdmsToFilter: string[];
  dmsToFilter: string[];
  dateFilterValue: string | null;
  accountStatus: AccountStatus | null;
}): GetAccountsData => {
  return {
    fetchAllMembers: true,
    departmentFilters: {
      pdmId: pdmsToFilter.length > 0 ? pdmsToFilter : undefined,
      dmId: dmsToFilter.length > 0 ? dmsToFilter : undefined,
      ...(accountStatus && {
        status: accountStatus,
      }),
    },
    ...(dateFilterValue && {
      periodFiltering: {
        from: dateFilterValue,
        to: dateFilterValue,
      },
    }),
  };
};
